/**
 * Laying out regular or randomized tile patterns on a 2D plane.
 * @module
 */
import degreeToRadian from "common/utils/degreeToRadian";
import { unsignedDeltaDegrees } from "common/utils/math";
import { RandomSourceWithSeed } from "common/utils/random";
import type { AABB2 } from "geom/aabb2Types";
import type { Mat3 } from "geom/mat3";
import { mat3Dot, mat3Rotate } from "geom/mat3";
import { polyLine2ConservativeRasterFill } from "geom/offset";
import { polyLine2New } from "geom/polyLine2Types";
import { vec2DistSquared } from "geom/vec2";
import { MAX_SDF_PATTERNS } from "../designMaterialDefaults";
import { sdfGridToUvTransform, uvToSdfGridTransform } from "../designMaterialParams";
import type {
  DmSplitDirections,
  SdfCommonParamsPreprocessed,
  SdfPatternPreprocessed,
  SdfRandomTilesParams,
} from "../designMaterialTypes";

/**
 * Return all SDF primary grid tiles that intersect with a given AABB in uv space.
 * @param pattern SDF pattern
 * @param minU
 * @param maxU
 * @param minV
 * @param maxV
 * @param wrapRandom
 * @param randomSeed
 */
export function* sdfTileLocationsInAABB(
  pattern: SdfPatternPreprocessed,
  minU: number,
  maxU: number,
  minV: number,
  maxV: number,
  randomSeed: number = 1234567,
): Generator<number[]> {
  const uvToGrid = uvToSdfGridTransform(pattern);
  const gridToUvNoRotate = sdfGridToUvTransform(pattern, false);

  // Compute where the corners of the AABB fall on the SDF grid.
  const aabbInSdfCoordinates = polyLine2New([
    ...mat3Dot(uvToGrid, minU, minV),
    ...mat3Dot(uvToGrid, maxU, minV),
    ...mat3Dot(uvToGrid, maxU, maxV),
    ...mat3Dot(uvToGrid, minU, maxV),
    ...mat3Dot(uvToGrid, minU, minV),
  ]);

  // This seeded random generator is used when generating heightmaps, so that we get the same pattern with the same parameters.
  // This makes sure that the generated heightmaps are predictable and don't jump around when parameters are edited.
  const randomSource = new RandomSourceWithSeed(randomSeed);

  // Conservatively rasterize the AABB on the SDF grid to get the grid squares that fall under the AABB.
  const rasterCoordGen = polyLine2ConservativeRasterFill(aabbInSdfCoordinates);
  let rasterCoord = rasterCoordGen.next();
  while (!rasterCoord.done) {
    const [x, y] = mat3Dot(gridToUvNoRotate, rasterCoord.value[0] + 0.5, rasterCoord.value[1] + 0.5);
    const rand = randomSource.next();
    const randMask = randomSource.next();
    yield [x, y, rasterCoord.value[0], rasterCoord.value[1], rand, randMask];
    rasterCoord = rasterCoordGen.next();
  }
}

/**
 * Lay out sdf cells in a regular grid pattern.
 * The returned SDF cell center and size don't have rotation applied.
 * @param pattern
 * @param uvAABB
 */
function* sdfRegularCellBoundariesInAABB(pattern: SdfPatternPreprocessed, uvAABB: AABB2): Generator<number[]> {
  // We use a seeded RNG so that we get the same pattern with the same parameters.
  // This makes sure that the generated heightmaps are predictable and don't jump around when parameters are edited.
  const SDF_LAYOUT_RANDOM_SEEDS = [
    1234567, 78897, 34581, 834908, 10389, 456801, 240534, 983453, 21353, 603548, 73478, 218564,
  ];
  if (!(MAX_SDF_PATTERNS === SDF_LAYOUT_RANDOM_SEEDS.length)) {
    console.assert(false);
  }
  const randomSeed = SDF_LAYOUT_RANDOM_SEEDS[pattern.maskRandomSeedIndex];
  const tileLocations = sdfTileLocationsInAABB(pattern, uvAABB.minX, uvAABB.maxX, uvAABB.minY, uvAABB.maxY, randomSeed);
  let tileLocation = tileLocations.next();

  while (!tileLocation.done) {
    const [cx, cy, , , cellRand, cellRandMask] = tileLocation.value as number[];
    yield [cx, cy, pattern.xSize, pattern.ySize, cellRand, cellRandMask];
    tileLocation = tileLocations.next();
  }
}

function sparseGridKey(x: number, y: number) {
  return x + "," + y;
}

function sparseGridCellFromKey(gridKey: string) {
  const [xString, yString] = gridKey.split(",");
  const x = parseInt(xString);
  const y = parseInt(yString);
  return [x, y];
}

/**
 * Get borders of an SDF cell in the random split pattern by querying the splits in 4 adjacent primary grid tiles.
 *
 * The big squares are primary grid tiles, the generated SDF cell is filled with Xes.
 *
 * Split            Split
 * vertically:      horizontally:
 * *--------------* *--------------*
 * |         |    | |   |          |
 * |         |    | |   |          |
 * |---------|    | |   |          |
 * |         |----| |--------------|
 * |         |XXXX| |XXXXXX|       |
 * |         |XXXX| |XXXXXX|       |
 * |         |XXXX| |XXXXXX|       |
 * *--------------* *--------------*
 *
 * *--------------* *--------------*
 * |         |XXXX| |XXXXXX|       |
 * |         |XXXX| |XXXXXX|-------|
 * |         |XXXX| |XXXXXX|       |
 * |--------------| |------|       |
 * |   |          | |      |       |
 * |   |          | |      |       |
 * |   |          | |      |       |
 * *--------------* *--------------*
 * Split            Split
 * horizontally     vertically
 *  */
function getSDFRandomSplitCell(
  gridX: number,
  gridY: number,
  pattern: SdfPatternPreprocessed,
  gridRandom: (x, y) => number,
): number[] {
  // Every other tile is split vertically in a checkerboard pattern:
  const splitIsVertical = (gridX + gridY) % 2 === 0;

  // The cells in this pattern have the same amount of "grout" between them as in the primary grid pattern.
  const insetX = (pattern.xDistance - pattern.xSize) * 0.5;
  const insetY = (pattern.yDistance - pattern.ySize) * 0.5;
  const leftEdge = (gridX + gridRandom(gridX, gridY + (splitIsVertical ? 0 : 1))) * pattern.xDistance + insetX;
  const rightEdge = (gridX + 1 + gridRandom(gridX + 1, gridY + (splitIsVertical ? 1 : 0))) * pattern.xDistance - insetX;
  const topEdge = (gridY + gridRandom(gridX + (splitIsVertical ? 1 : 0), gridY)) * pattern.yDistance + insetY;
  const bottomEdge =
    (gridY + 1 + gridRandom(gridX + (splitIsVertical ? 0 : 1), gridY + 1)) * pattern.yDistance - insetY;
  const centerX = (leftEdge + rightEdge) * 0.5;
  const centerY = (topEdge + bottomEdge) * 0.5;
  const xSize = rightEdge - leftEdge;
  const ySize = bottomEdge - topEdge;
  return [centerX, centerY, xSize, ySize];
}

/**
 * Lay out SDF cells in a pattern where each primary grid tile is split either horizontally or vertically at a semi-random point.
 * The returned SDF cell center and size don't have rotation applied.
 * @param sdf SDF parameters.
 * @param pattern SDF pattern.
 * @param uvAABB Area in UV space to fill.
 * @param wrapSizeU Set to wrap the pattern at a certain coordinate. This should be a multiple of pattern.xDistance.
 * @param wrapSizeV Set to wrap the pattern at a certain coordinate. This should be a multiple of pattern.yDistance.
 */
function* sdfRandomSplitCellBoundariesInAABB(
  sdfRandomTiles: SdfRandomTilesParams,
  pattern: SdfPatternPreprocessed,
  uvAABB: AABB2,
  wrapSizeU?: number,
  wrapSizeV?: number,
): Generator<number[]> {
  const wrapRandom = wrapSizeU !== undefined && wrapSizeV !== undefined;
  const wrapGridX = wrapRandom ? wrapSizeU / pattern.xDistance : 0;
  const wrapGridY = wrapRandom ? wrapSizeV / pattern.yDistance : 0;

  function wrapX(x) {
    if (x >= wrapGridX) {
      return x - wrapGridX;
    }
    if (x < 0) {
      return x + wrapGridX;
    }
    return x;
  }
  function wrapY(y) {
    if (y >= wrapGridY) {
      return y - wrapGridY;
    }
    if (y < 0) {
      return y + wrapGridY;
    }
    return y;
  }

  const gridRandomRaw = (x, y, extraSeed = 0) => {
    if (wrapRandom) {
      x = wrapX(x);
      y = wrapY(y);
    }
    const src = new RandomSourceWithSeed(x * 16453 + y * 67 + extraSeed);
    return src.next();
  };
  const gridRandomTileVariability = (x, y, extraSeed = 0) => {
    const minRandomness = sdfRandomTiles.minTileVariability * sdfRandomTiles.tileVariability;
    const maxRandomness = sdfRandomTiles.tileVariability;
    const randomRaw = gridRandomRaw(x, y, extraSeed);
    if (wrapRandom) {
      x = wrapX(x);
      y = wrapY(y);
    }
    const usePositiveOffset = (Math.floor(x / 2) + Math.floor(y / 2)) % 2 === 0;
    const normalizedRandomOffset =
      (usePositiveOffset ? 1 : -1) *
      (minRandomness + Math.abs(randomRaw - 0.5) * 2.0 * (maxRandomness - minRandomness));
    return 0.5 + normalizedRandomOffset * 0.5;
  };

  const xOffset = pattern.xOffset;
  const yOffset = pattern.xOffset;
  pattern.xOffset = 0;
  pattern.yOffset = 0;

  const tileLocations = sdfTileLocationsInAABB(pattern, uvAABB.minX, uvAABB.maxX, uvAABB.minY, uvAABB.maxY, 1234567);
  let tileLocation = tileLocations.next();

  pattern.xOffset = xOffset;
  pattern.yOffset = yOffset;

  const sparseGrid: Set<string> = new Set();

  while (!tileLocation.done) {
    const [, , idX, idY] = tileLocation.value as number[];
    // Make sure that adjacent cells are included.
    sparseGrid.add(sparseGridKey(idX, idY));
    sparseGrid.add(sparseGridKey(idX - 1, idY));
    sparseGrid.add(sparseGridKey(idX, idY - 1));
    sparseGrid.add(sparseGridKey(idX - 1, idY - 1));
    tileLocation = tileLocations.next();
  }

  function respectMinSize(splitSize1: number, splitSize2: number, minSize: number): number[] {
    let useAverageSize = false;
    if (splitSize1 < minSize) {
      const changeToMeetMinSize = minSize - splitSize1;
      if (splitSize2 - changeToMeetMinSize < minSize) {
        useAverageSize = true;
      } else {
        splitSize1 += changeToMeetMinSize;
        splitSize2 -= changeToMeetMinSize;
      }
    } else if (splitSize2 < minSize) {
      const changeToMeetMinHeight = minSize - splitSize2;
      if (splitSize1 - changeToMeetMinHeight < minSize) {
        useAverageSize = true;
      } else {
        splitSize2 += changeToMeetMinHeight;
        splitSize1 -= changeToMeetMinHeight;
      }
    }
    if (useAverageSize) {
      splitSize1 = (splitSize1 + splitSize2) / 2;
      splitSize2 = splitSize1;
    }
    return [splitSize1, splitSize2];
  }

  for (const key of sparseGrid.values()) {
    const [gridX, gridY] = sparseGridCellFromKey(key);
    const [centerX, centerY, xSize, ySize] = getSDFRandomSplitCell(gridX, gridY, pattern, gridRandomTileVariability);
    if (xSize > 0 && ySize > 0) {
      const splitX = xSize > sdfRandomTiles.alwaysSplitTilesAboveWidth;
      const splitY = ySize > sdfRandomTiles.alwaysSplitTilesAboveHeight;
      if (
        sdfRandomTiles.tileSplitDirection !== "None" &&
        (sdfRandomTiles.tileSplitProbability > gridRandomRaw(gridX, gridY, 11723) || splitX || splitY)
      ) {
        const splitOffset = 0.5 + (gridRandomRaw(gridX, gridY, 34019) - 0.5) * sdfRandomTiles.tileVariability;
        let tileSplitDirection: (typeof DmSplitDirections)[number] = sdfRandomTiles.tileSplitDirection;
        if (splitX && !splitY) {
          tileSplitDirection = "X";
        } else if (splitY && !splitX) {
          tileSplitDirection = "Y";
        } else if (tileSplitDirection === "LongerAxis") {
          if (xSize > ySize) {
            tileSplitDirection = "X";
          } else {
            tileSplitDirection = "Y";
          }
        } else if (tileSplitDirection === "Random") {
          tileSplitDirection = gridRandomRaw(gridX, gridY, 4277) < 0.5 ? "X" : "Y";
        }
        if (tileSplitDirection === "X") {
          const insetX = (pattern.xDistance - pattern.xSize) * 0.5;
          let splitXSize1 = xSize * splitOffset - insetX;
          let splitXSize2 = xSize - splitXSize1 - insetX * 2;
          [splitXSize1, splitXSize2] = respectMinSize(splitXSize1, splitXSize2, sdfRandomTiles.minTileWidth);
          const splitCenterX1 = centerX - xSize * 0.5 + splitXSize1 * 0.5;
          const splitCenterX2 = centerX + xSize * 0.5 - splitXSize2 * 0.5;
          yield [
            splitCenterX1,
            centerY,
            splitXSize1,
            ySize,
            gridRandomRaw(gridX, gridY, 23498),
            gridRandomRaw(gridX, gridY, 978932),
          ];
          yield [
            splitCenterX2,
            centerY,
            splitXSize2,
            ySize,
            gridRandomRaw(gridX, gridY, 78902),
            gridRandomRaw(gridX, gridY, 76823),
          ];
        } else if (tileSplitDirection === "Y") {
          const insetY = (pattern.yDistance - pattern.ySize) * 0.5;
          let splitYSize1 = ySize * splitOffset - insetY;
          let splitYSize2 = ySize - splitYSize1 - insetY * 2;
          [splitYSize1, splitYSize2] = respectMinSize(splitYSize1, splitYSize2, sdfRandomTiles.minTileHeight);
          const splitCenterY1 = centerY - ySize * 0.5 + splitYSize1 * 0.5;
          const splitCenterY2 = centerY + ySize * 0.5 - splitYSize2 * 0.5;
          yield [
            centerX,
            splitCenterY1,
            xSize,
            splitYSize1,
            gridRandomRaw(gridX, gridY, 23498),
            gridRandomRaw(gridX, gridY, 978932),
          ];
          yield [
            centerX,
            splitCenterY2,
            xSize,
            splitYSize2,
            gridRandomRaw(gridX, gridY, 78902),
            gridRandomRaw(gridX, gridY, 76823),
          ];
        }
      } else {
        yield [centerX, centerY, xSize, ySize, gridRandomRaw(gridX, gridY, 23498), gridRandomRaw(gridX, gridY, 978932)];
      }
    }
  }
}

/**
 * Lay out SDF cells inside an AABB according to the selected pattern.
 * The returned SDF cell center is rotated into place, but the cell size doesn't have rotation applied.
 * @param sdfCommon SDF parameters.
 * @param pattern SDF pattern.
 * @param _patternIndex SDF pattern index.
 * @param uvAABB Area in UV space to fill.
 * @param wrapRandom Whether to wrap the random values that are generated for each cell.
 * @param wrapSizeU Set to wrap the pattern at a certain coordinate. This should be a multiple of pattern.xDistance.
 * @param wrapSizeV Set to wrap the pattern at a certain coordinate. This should be a multiple of pattern.yDistance.
 */
export function* sdfCellBoundariesInAABB(
  sdfCommon: SdfCommonParamsPreprocessed,
  sdfRandomTiles: SdfRandomTilesParams,
  pattern: SdfPatternPreprocessed,
  _patternIndex: number,
  uvAABB: AABB2,
  wrapRandom: boolean,
  wrapSizeU?: number,
  wrapSizeV?: number,
): Generator<number[]> {
  let cellBoundaries;
  // Setting defaults mostly to satisfy strict null checks
  wrapSizeU = wrapSizeU ?? 1;
  wrapSizeV = wrapSizeV ?? 1;
  if (sdfCommon.randomTilesMode === "RandomSplit") {
    cellBoundaries = sdfRandomSplitCellBoundariesInAABB(
      sdfRandomTiles,
      pattern,
      uvAABB,
      wrapRandom ? wrapSizeU : undefined,
      wrapRandom ? wrapSizeV : undefined,
    );
  } else {
    cellBoundaries = sdfRegularCellBoundariesInAABB(pattern, uvAABB);
  }

  const edgeCells: number[][] = [];
  const minU = uvAABB.minX;
  const maxU = uvAABB.maxX;
  const minV = uvAABB.minY;
  const maxV = uvAABB.maxY;
  const maxSize = Math.sqrt(pattern.xSize * pattern.xSize + pattern.ySize * pattern.ySize);

  const matRotate: Mat3 = mat3Rotate(degreeToRadian(pattern.rotation));
  let cellAABB: IteratorResult<number[], any> = cellBoundaries.next();
  while (!cellAABB.done) {
    const [centerX, centerY, xSize, ySize] = cellAABB.value;
    let rand = cellAABB.value[4];
    let randMask = cellAABB.value[5];
    let wrappingCellX = 0;
    let wrappingCellY = 0;
    const [centerU, centerV] = mat3Dot(matRotate, centerX, centerY);
    // Wrap rand and randMask at the edges of the AABB.
    if (wrapRandom) {
      let foundWrap = false;
      const wrapToleranceSquared = Math.pow(0.1, 2);
      // TODO: Detect edge cells based on wrapSize, not the uv AABB - these are different in case of the hatch material generation.
      if (centerU < minU + maxSize) {
        // This element might intersect the edge of the AABB, so we look if we'll find a matching element on the other side.
        for (const edgeCell of edgeCells) {
          if (vec2DistSquared(edgeCell[0], edgeCell[1], centerU + wrapSizeU, centerV) < wrapToleranceSquared) {
            rand = edgeCell[2];
            randMask = edgeCell[3];
            foundWrap = true;
            wrappingCellX = -1;
            break;
          }
        }
      }
      if (!foundWrap && centerV < minV + maxSize) {
        for (const edgeCell of edgeCells) {
          if (vec2DistSquared(edgeCell[0], edgeCell[1], centerU, centerV + wrapSizeV) < wrapToleranceSquared) {
            rand = edgeCell[2];
            randMask = edgeCell[3];
            foundWrap = true;
            wrappingCellY = -1;
            break;
          }
        }
      }
      if (!foundWrap && centerU > maxU - maxSize) {
        for (const edgeCell of edgeCells) {
          if (vec2DistSquared(edgeCell[0], edgeCell[1], centerU - wrapSizeU, centerV) < wrapToleranceSquared) {
            rand = edgeCell[2];
            randMask = edgeCell[3];
            foundWrap = true;
            wrappingCellX = 1;
            break;
          }
        }
      }
      if (!foundWrap && centerV > maxV - maxSize) {
        for (const edgeCell of edgeCells) {
          if (vec2DistSquared(edgeCell[0], edgeCell[1], centerU, centerV - wrapSizeV) < wrapToleranceSquared) {
            rand = edgeCell[2];
            randMask = edgeCell[3];
            foundWrap = true;
            wrappingCellY = 1;
            break;
          }
        }
      }
      if (!foundWrap && centerU < minU + maxSize && centerV < minV + maxSize) {
        for (const edgeCell of edgeCells) {
          if (
            vec2DistSquared(edgeCell[0], edgeCell[1], centerU + wrapSizeU, centerV + wrapSizeV) < wrapToleranceSquared
          ) {
            rand = edgeCell[2];
            randMask = edgeCell[3];
            foundWrap = true;
            wrappingCellX = -1;
            wrappingCellY = -1;
            break;
          }
        }
      }
      if (!foundWrap && centerU > maxU - maxSize && centerV < minV + maxSize) {
        for (const edgeCell of edgeCells) {
          if (
            vec2DistSquared(edgeCell[0], edgeCell[1], centerU - wrapSizeU, centerV + wrapSizeV) < wrapToleranceSquared
          ) {
            rand = edgeCell[2];
            randMask = edgeCell[3];
            foundWrap = true;
            wrappingCellX = 1;
            wrappingCellY = -1;
            break;
          }
        }
      }
      if (!foundWrap && centerU < minU + maxSize && centerV > maxV - maxSize) {
        for (const edgeCell of edgeCells) {
          if (
            vec2DistSquared(edgeCell[0], edgeCell[1], centerU + wrapSizeU, centerV - wrapSizeV) < wrapToleranceSquared
          ) {
            rand = edgeCell[2];
            randMask = edgeCell[3];
            foundWrap = true;
            wrappingCellX = -1;
            wrappingCellY = 1;
            break;
          }
        }
      }
      if (!foundWrap && centerU > maxU - maxSize && centerV > maxV - maxSize) {
        for (const edgeCell of edgeCells) {
          if (
            vec2DistSquared(edgeCell[0], edgeCell[1], centerU - wrapSizeU, centerV - wrapSizeV) < wrapToleranceSquared
          ) {
            rand = edgeCell[2];
            randMask = edgeCell[3];
            foundWrap = true;
            wrappingCellX = 1;
            wrappingCellY = 1;
            break;
          }
        }
      }
      if (
        !foundWrap &&
        (centerU < minU + maxSize || centerV < minV + maxSize || centerU > maxU - maxSize || centerV > maxV - maxSize)
      ) {
        edgeCells.push([centerU, centerV, rand, randMask]);
      }
    }
    // The wrappingCellX and wrappingCellY values are intended for when the cells are rendered into a tileable texture.
    //
    // The outlined areas in the illustration below are instances of the texture when rendered.
    // The numbered areas in the illustration mark an SDF cell that wraps around the edge of the texture.
    // The numbers correspond to the wrappingCellX values.
    //
    //       INSTANCE 1     INSTANCE 2
    //       OF TILING      OF TILING
    //        TEXTURE        TEXTURE
    //    *--------------*--------------*
    //    |              |              |
    //    |00000      111|00000      111|
    //    |00000      111|00000      111|
    //    |00000      111|00000      111|
    //    |00000      111|00000      111|
    //    |              |              |
    //    *--------------*--------------*
    //
    // These values can be used in a shader to assign each cell to an instance of the texture, even if the cell wraps around the texture edge.
    // The shader first figures out the texture instance based on the UV coordinates, and then offsets that number by the wrappingCellX and wrappingCellY values.
    //
    //    *--------------*--------------*
    //    |              |              |
    // #########      #########      #########
    // |Part of|      |Part of|      |Part of|
    // INSTANCE1      INSTANCE2      INSTANCE3
    // #########      #########      #########
    //    |              |              |
    //    *--------------*--------------*
    //
    // -2 is a special wrappingCell value: it marks an SDF cell that's continuous.
    //
    // The wrappingCell values can be either -2, -1, 0 or 1.
    // Only one of the copies of the same cell has a non-zero wrappingCell value.
    //

    const notRotatedContinuous =
      unsignedDeltaDegrees(pattern.rotation, 0) < 0.001 || unsignedDeltaDegrees(pattern.rotation, 180) < 0.001;
    const rotated90Continuous =
      unsignedDeltaDegrees(pattern.rotation, 90) < 0.001 || unsignedDeltaDegrees(pattern.rotation, -90) < 0.001;
    const irregularRotation = !notRotatedContinuous && !rotated90Continuous;

    if ((pattern.continuousX && notRotatedContinuous) || (pattern.continuousY && rotated90Continuous)) {
      wrappingCellX = -2;
    }
    if ((pattern.continuousY && notRotatedContinuous) || (pattern.continuousX && rotated90Continuous)) {
      wrappingCellY = -2;
    }
    if ((pattern.continuousX || pattern.continuousY) && irregularRotation) {
      wrappingCellX = -2;
      wrappingCellY = -2;
    }

    yield [centerU, centerV, xSize, ySize, rand, randMask, wrappingCellX, wrappingCellY];
    cellAABB = cellBoundaries.next();
  }
}
